import React, { useState, useRef, useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import "@/App.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { FileDown, RotateCcw, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, Zap, AlertTriangle, CheckCircle, LogIn, LogOut, Shield, User, FolderOpen, Save, Flame, BarChart2 } from "lucide-react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AuthPage from "./components/AuthPage";
import ProjectsPanel from "./components/ProjectsPanel";
import UserDashboard from "./components/UserDashboard";
import ClientTypeSelector from "./components/calculateur/ClientTypeSelector";
import EnergyTypeSelector from "./components/calculateur/EnergyTypeSelector";
import ParticulierSection from "./components/calculateur/ParticulierSection";
import InformationsGenerales from "./components/calculateur/InformationsGenerales";
import AcheminementSection from "./components/calculateur/AcheminementSection";
import AcciseSection from "./components/calculateur/AcciseSection";
import EffacementSection from "./components/calculateur/EffacementSection";
import ComparatifSection from "./components/calculateur/ComparatifSection";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import logger from './lib/logger';
import { calculTURPE, SEGMENTS_ELEC } from './lib/turpe';
import { calculATRD, estNafIndustrielGaz, ATRD, ACCISE_GAZ_TAUX_STANDARD, ACCISE_GAZ_TAUX_REDUIT } from './lib/atrd';

// AdminPage est code-splitté : ~1 432 lignes + recharts imports (~30 KB après gzip).
// Ce bundle n'est téléchargé qu'à la navigation vers /admin.
const AdminPage = lazy(() => import("./components/AdminPage"));

// URL du logo Place des Énergies (local)
const LOGO_URL = "/logo-pde.png";
// Use relative URL in production, env variable in development/preview
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Composant principal enveloppé avec AuthProvider et Router
function AppWrapper() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<div style={{ padding: '4rem', textAlign: 'center', color: '#64748B' }}>Chargement…</div>}>
          <Routes>
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/*" element={<App />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

function App() {
  const navigate = useNavigate();
  // Auth state
  const { user, token, isAuthenticated, isAdmin, logout, loading: authLoading } = useAuth();
  const [showProjectsPanel, setShowProjectsPanel] = useState(false);
  const [showUserDashboard, setShowUserDashboard] = useState(false);

  // ============================================
  // SÉLECTION TYPE CLIENT (Professionnel / Particulier)
  // ============================================
  const [clientType, setClientType] = useState("pro"); // "pro" ou "particulier"

  // ============================================
  // SÉLECTION ÉNERGIE (Électricité / Gaz)
  // ============================================
  const [energyType, setEnergyType] = useState("electricity"); // "electricity" ou "gas"

  // ============================================
  // PARTICULIER : Sélection HT/TTC pour l'affichage
  // ============================================
  const [particulierPriceDisplay, setParticulierPriceDisplay] = useState("ht"); // "ht" ou "ttc"

  // ============================================
  // PARTICULIER : Postes de consommation spécifiques
  // Tous les postes possibles sont stockés ici. L'offre courante détermine
  // ceux qui sont affichés dans le tableau.
  // ============================================
  const [postesParticulier, setPostesParticulier] = useState([
    // Offre standard / HP-HC
    { id: "BASE", label: "Base", conso: "", prixActuel: "", prixFutur: "" },
    { id: "HSP", label: "Heure Super Pleine", conso: "", prixActuel: "", prixFutur: "" },
    { id: "HP", label: "Heure Pleine", conso: "", prixActuel: "", prixFutur: "" },
    { id: "HC", label: "Heure Creuse", conso: "", prixActuel: "", prixFutur: "" },
    { id: "SUPER_CREUSE", label: "Super Creuse", conso: "", prixActuel: "", prixFutur: "" },
    // Offre Tempo - 6 plages couleurs
    { id: "TEMPO_ROUGE_HP", label: "Jour Rouge - Heure Pleine", conso: "", prixActuel: "", prixFutur: "" },
    { id: "TEMPO_ROUGE_HC", label: "Jour Rouge - Heure Creuse", conso: "", prixActuel: "", prixFutur: "" },
    { id: "TEMPO_BLANC_HP", label: "Jour Blanc - Heure Pleine", conso: "", prixActuel: "", prixFutur: "" },
    { id: "TEMPO_BLANC_HC", label: "Jour Blanc - Heure Creuse", conso: "", prixActuel: "", prixFutur: "" },
    { id: "TEMPO_BLEU_HP", label: "Jour Bleu - Heure Pleine", conso: "", prixActuel: "", prixFutur: "" },
    { id: "TEMPO_BLEU_HC", label: "Jour Bleu - Heure Creuse", conso: "", prixActuel: "", prixFutur: "" },
    // Offre Zen Week-End - 4 plages
    { id: "ZEN_HP_SEMAINE", label: "HP Semaine", conso: "", prixActuel: "", prixFutur: "" },
    { id: "ZEN_HC_SEMAINE", label: "HC Semaine", conso: "", prixActuel: "", prixFutur: "" },
    { id: "ZEN_HP_WEEKEND", label: "HP Week-End", conso: "", prixActuel: "", prixFutur: "" },
    { id: "ZEN_HC_WEEKEND", label: "HC Week-End", conso: "", prixActuel: "", prixFutur: "" },
  ]);

  // PARTICULIER : Offre tarifaire courante
  // 'standard' = Base / HSP / HP / HC / Super Creuse
  // 'tempo'    = Tempo (6 plages couleurs)
  // 'zen'      = Zen Week-End (4 plages semaine/week-end)
  const [offreParticulier, setOffreParticulier] = useState("standard");
  
  // PARTICULIER : Abonnement annuel
  const [abonnementParticulier, setAbonnementParticulier] = useState({ prixActuel: "", prixFutur: "" });
  
  // PARTICULIER : Gaz (un seul poste + abonnement)
  const [consoGazParticulier, setConsoGazParticulier] = useState({ conso: "", prixActuel: "", prixFutur: "" });
  const [abonnementGazParticulier, setAbonnementGazParticulier] = useState({ prixActuel: "", prixFutur: "" });

  // PARTICULIER : Métadonnées site et fournisseurs
  const [adresseSite, setAdresseSite] = useState("");
  const [pdlParticulier, setPdlParticulier] = useState("");
  const [fournisseurActuel, setFournisseurActuel] = useState("");
  const [fournisseurFutur, setFournisseurFutur] = useState("");

  // Mapping offre → liste des IDs de postes affichés (mémorisé module-level pour stabilité)
  const POSTES_PAR_OFFRE = {
    standard: ["BASE", "HSP", "HP", "HC", "SUPER_CREUSE"],
    tempo: ["TEMPO_ROUGE_HP", "TEMPO_ROUGE_HC", "TEMPO_BLANC_HP", "TEMPO_BLANC_HC", "TEMPO_BLEU_HP", "TEMPO_BLEU_HC"],
    zen: ["ZEN_HP_SEMAINE", "ZEN_HC_SEMAINE", "ZEN_HP_WEEKEND", "ZEN_HC_WEEKEND"],
  };
  const postesAffichesParticulier = postesParticulier.filter(p =>
    (POSTES_PAR_OFFRE[offreParticulier] || POSTES_PAR_OFFRE.standard).includes(p.id)
  );

  // États pour les informations générales
  const [clientName, setClientName] = useState("");
  const [commercialName, setCommercialName] = useState("");
  const [nafCode, setNafCode] = useState("");
  const [optionTarifaire, setOptionTarifaire] = useState("");
  const [segmentElec, setSegmentElec] = useState(""); // C1, C2, C3, C4, C5

  // États spécifiques au Gaz
  const [segmentGaz, setSegmentGaz] = useState("T1"); // T1, T2, T3, T4
  const [capaciteJournaliereT4, setCapaciteJournaliereT4] = useState("");
  const [niveauCapaciteT4, setNiveauCapaciteT4] = useState("T4_low"); // T4_low ou T4_high
  const [forceAcciseGazTauxPlein, setForceAcciseGazTauxPlein] = useState(false);

  // État pour le segment gaz futur (pour comparaison acheminement)
  const [segmentGazFutur, setSegmentGazFutur] = useState("T1"); // Initialisé à T1, sera synchronisé avec segmentGaz
  const [capaciteJournaliereT4Futur, setCapaciteJournaliereT4Futur] = useState("");
  const [niveauCapaciteT4Futur, setNiveauCapaciteT4Futur] = useState("T4_low");

  // États pour la consommation par poste (Électricité)
  const [postes, setPostes] = useState([
    { id: "POINTE", label: "Pointe", conso: "", prixActuel: "", prixFutur: "", prixCourtage: "" },
    { id: "HPH", label: "HPH", conso: "", prixActuel: "", prixFutur: "", prixCourtage: "" },
    { id: "HCH", label: "HCH", conso: "", prixActuel: "", prixFutur: "", prixCourtage: "" },
    { id: "HPE", label: "HPE", conso: "", prixActuel: "", prixFutur: "", prixCourtage: "" },
    { id: "HCE", label: "HCE", conso: "", prixActuel: "", prixFutur: "", prixCourtage: "" },
  ]);
  
  // Type de budget sélectionné : "actuel" ou "courtage"
  const [typeBudgetConso, setTypeBudgetConso] = useState("actuel");
  const [typeBudgetAboPrest, setTypeBudgetAboPrest] = useState("actuel");

  // État pour la consommation Gaz (un seul poste)
  const [consoGaz, setConsoGaz] = useState({ conso: "", prixActuel: "", prixFutur: "" });

  // États pour abonnement et prestations - INDÉPENDANTS entre Élec et Gaz
  const [abonnement, setAbonnement] = useState({ quantite: 1, prixActuel: "", prixFutur: "", prixCourtage: "" });
  const [prestation, setPrestation] = useState({ quantite: 1, prixActuel: "", prixFutur: "", prixCourtage: "" });
  const [abonnementGaz, setAbonnementGaz] = useState({ quantite: 1, prixActuel: "", prixFutur: "", prixCourtage: "" });
  const [prestationGaz, setPrestationGaz] = useState({ quantite: 1, prixActuel: "", prixFutur: "", prixCourtage: "" });
  // Sélecteurs courants (selon le type d'énergie sélectionné)
  const currentAbonnement = energyType === 'gas' ? abonnementGaz : abonnement;
  const setCurrentAbonnement = energyType === 'gas' ? setAbonnementGaz : setAbonnement;
  const currentPrestation = energyType === 'gas' ? prestationGaz : prestation;
  const setCurrentPrestation = energyType === 'gas' ? setPrestationGaz : setPrestation;

  // État pour la puissance souscrite (utilisée pour Accise et Effacement) - Électricité uniquement
  const [puissanceSouscriteInput, setPuissanceSouscriteInput] = useState("");
  const [puissanceAtteinteInput, setPuissanceAtteinteInput] = useState("");

  // États pour sections optionnelles
  const [showAcheminement, setShowAcheminement] = useState(false);
  const [acheminement, setAcheminement] = useState({ prixActuel: "", prixFutur: "" });
  const [showAccise, setShowAccise] = useState(false);
  const [accise, setAccise] = useState({ prixActuel: "", prixFutur: "" });
  const [forceTauxPlein, setForceTauxPlein] = useState(false); // Pour forcer le taux plein même si NAF 4-39

  // États pour l'estimation effacement électrique (Électricité uniquement)
  const [showEffacement, setShowEffacement] = useState(false);
  const [effacement, setEffacement] = useState({ tauxEffacement: "20" });

  // État pour l'optimisation Energy Manager (5% du budget global)
  const [showEnergyManager, setShowEnergyManager] = useState(false);

  // Logo en base64 pour le PDF (chargé une fois au montage)
  const [logoBase64, setLogoBase64] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch(LOGO_URL);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      } catch (error) {
        logger.error("Error loading logo:", error);
      }
    };
    loadLogo();
  }, []);

  // Heartbeat pour le suivi en temps réel (toutes les 30 secondes)
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    
    const sendHeartbeat = async () => {
      try {
        await fetch(`${API_URL}/api/activity/heartbeat`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } catch (error) {
        // Heartbeat failures are non-critical, log only in development
        if (process.env.NODE_ENV === 'development') {
          logger.warn('Heartbeat failed:', error.message);
        }
      }
    };
    
    // Send immediately on login
    sendHeartbeat();
    
    // Then every 30 seconds
    const interval = setInterval(sendHeartbeat, 30000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, token]);

  // Synchroniser le segment futur avec le segment actuel par défaut
  useEffect(() => {
    setSegmentGazFutur(segmentGaz);
    setCapaciteJournaliereT4Futur(capaciteJournaliereT4);
    setNiveauCapaciteT4Futur(niveauCapaciteT4);
  }, [segmentGaz, capaciteJournaliereT4, niveauCapaciteT4]);

  // Calculs
  const calculateBudget = (conso, prix) => {
    const c = parseFloat(conso) || 0;
    const p = parseFloat(prix) || 0;
    return c * p;
  };

  // Calcul du prix actuel effectif selon le type global sélectionné
  const getPrixActuelEffectif = (poste) => {
    if (typeBudgetConso === "courtage") {
      return parseFloat(poste.prixCourtage) || 0;
    }
    return parseFloat(poste.prixActuel) || 0;
  };

  // Total consommation actuelle avec prise en compte du type de prix
  const calculateTotalActuel = (items) => {
    return items.reduce((sum, item) => {
      const conso = parseFloat(item.conso) || 0;
      const prix = getPrixActuelEffectif(item);
      return sum + (conso * prix);
    }, 0);
  };

  const calculateTotal = (items, field) => {
    return items.reduce((sum, item) => sum + calculateBudget(item.conso, item[field]), 0);
  };

  // Total consommation en MWh
  const totalConsoMWh = postes.reduce((sum, p) => sum + (parseFloat(p.conso) || 0), 0);

  // Totaux consommation (budget) - Actuel prend en compte le type sélectionné
  const totalConsoActuel = calculateTotalActuel(postes);
  const totalConsoFutur = calculateTotal(postes, "prixFutur");

  // Prix moyen pondéré (€/MWh)
  const prixMoyenActuel = totalConsoMWh > 0 ? totalConsoActuel / totalConsoMWh : 0;
  const prixMoyenFutur = totalConsoMWh > 0 ? totalConsoFutur / totalConsoMWh : 0;

  // Totaux abonnement & prestations - prend en compte le type sélectionné
  const getPrixAboPrestActuel = (item) => {
    if (typeBudgetAboPrest === "courtage") {
      return parseFloat(item.prixCourtage) || 0;
    }
    return parseFloat(item.prixActuel) || 0;
  };
  
  const totalAboPrestActuel = 
    getPrixAboPrestActuel(currentAbonnement) * currentAbonnement.quantite +
    getPrixAboPrestActuel(currentPrestation) * currentPrestation.quantite;
  const totalAboPrestFutur = 
    (parseFloat(currentAbonnement.prixFutur) || 0) * currentAbonnement.quantite +
    (parseFloat(currentPrestation.prixFutur) || 0) * currentPrestation.quantite;

  // Totaux optionnels
  // Puissance souscrite et atteinte (utilisées pour tous les calculs)
  const puissanceSouscrite = parseFloat(puissanceSouscriteInput) || 0;
  const puissanceAtteinte = parseFloat(puissanceAtteinteInput) || 0;

  // Constantes et fonction TURPE 7 importées depuis ./lib/turpe (module pur)
  // Cf. /app/frontend/src/lib/turpe.js — Délibération CRE 2025-78 du 13/03/2025

  // Consommation par période (en MWh)
  const consoPointe = parseFloat(postes.find(p => p.id === "POINTE")?.conso) || 0;
  const consoHPH = parseFloat(postes.find(p => p.id === "HPH")?.conso) || 0;
  const consoHCH = parseFloat(postes.find(p => p.id === "HCH")?.conso) || 0;
  const consoHPE = parseFloat(postes.find(p => p.id === "HPE")?.conso) || 0;
  const consoHCE = parseFloat(postes.find(p => p.id === "HCE")?.conso) || 0;
  
  const consoPeriods = {
    POINTE: consoPointe,
    HPH: consoHPH,
    HCH: consoHCH,
    HPE: consoHPE,
    HCE: consoHCE
  };
  
  // Calcul acheminement actuel (avec puissance souscrite)
  const acheminementActuelCalc = calculTURPE(puissanceSouscrite, optionTarifaire, consoPeriods, segmentElec);
  
  // Calcul acheminement futur (avec puissance atteinte si renseignée, sinon puissance souscrite)
  const puissanceFutur = puissanceAtteinte > 0 ? puissanceAtteinte : puissanceSouscrite;
  const acheminementFuturCalc = calculTURPE(puissanceFutur, optionTarifaire, consoPeriods, segmentElec);
  
  const totalAcheminementActuel = showAcheminement ? acheminementActuelCalc.total : 0;
  const totalAcheminementFutur = showAcheminement ? acheminementFuturCalc.total : 0;
  
  // ============================================
  // CALCUL ACCISE
  // ============================================
  
  // Calcul automatique de l'Accise basé sur la puissance souscrite et la consommation
  // Taux applicables
  const getTauxAccise = (puissanceKVA) => {
    if (puissanceKVA <= 36) {
      return 30.85; // 3 à 36 kVA : 30,85 €/MWh
    } else {
      return 26.58; // 37 kVA et plus : 26,58 €/MWh
    }
  };
  
  // Extraction du numéro du code NAF pour la logique conditionnelle
  const getNafNumber = (code) => {
    if (!code) return null;
    // Extraire les 2 premiers chiffres du code NAF (ex: "25.62A" -> 25, "04.00A" -> 4)
    const match = code.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  };
  
  const nafNumber = getNafNumber(nafCode);
  const isNafEntre4et39 = nafNumber !== null && nafNumber >= 4 && nafNumber <= 39;
  
  const tauxAccise = getTauxAccise(puissanceSouscrite);
  const acciseAutoActuel = totalConsoMWh * tauxAccise;
  
  // Budget futur Accise : calcul automatique basé sur le code NAF
  // Si NAF entre 4 et 39 ET pas forcé au taux plein : taux réduit de 0.5 €/MWh
  // Sinon (NAF < 4 ou > 39 ou non renseigné ou forcé taux plein) : même taux que le budget actuel
  const tauxAcciseFutur = (isNafEntre4et39 && !forceTauxPlein) ? 0.5 : tauxAccise;
  const acciseAutoFutur = totalConsoMWh * tauxAcciseFutur;
  
  const totalAcciseActuel = showAccise ? acciseAutoActuel : 0;
  const totalAcciseFutur = showAccise ? acciseAutoFutur : 0;

  // ============================================
  // CALCULS GAZ - ATRD7 + CTA + ACCISE (TICGN)
  // Constantes & fonctions importées depuis ./lib/atrd (module pur)
  // ============================================

  // Consommation gaz en MWh
  const consoGazMWh = parseFloat(consoGaz.conso) || 0;
  const capaciteT4 = parseFloat(capaciteJournaliereT4) || 0;
  const capaciteT4Fut = parseFloat(capaciteJournaliereT4Futur) || 0;

  const isNafIndustrielGaz = estNafIndustrielGaz(nafCode);

  // Calcul ATRD actuel (segment actuel) et futur (segment futur potentiellement différent)
  const atrdActuel = calculATRD(segmentGaz, consoGazMWh, capaciteT4, niveauCapaciteT4);
  const atrdFutur = calculATRD(segmentGazFutur, consoGazMWh, capaciteT4Fut, niveauCapaciteT4Futur);

  // Calcul Accise Gaz (TICGN)
  const tauxAcciseGazActuel = ACCISE_GAZ_TAUX_STANDARD;
  const tauxAcciseGazFutur = (isNafIndustrielGaz && !forceAcciseGazTauxPlein) ? ACCISE_GAZ_TAUX_REDUIT : ACCISE_GAZ_TAUX_STANDARD;
  
  const acciseGazActuel = consoGazMWh * tauxAcciseGazActuel;
  const acciseGazFutur = consoGazMWh * tauxAcciseGazFutur;

  // Budget consommation gaz
  const budgetConsoGazActuel = consoGazMWh * (parseFloat(consoGaz.prixActuel) || 0);
  const budgetConsoGazFutur = consoGazMWh * (parseFloat(consoGaz.prixFutur) || 0);

  // Prix moyen gaz
  const prixMoyenGazActuel = consoGazMWh > 0 ? (parseFloat(consoGaz.prixActuel) || 0) : 0;
  const prixMoyenGazFutur = consoGazMWh > 0 ? (parseFloat(consoGaz.prixFutur) || 0) : 0;

  // Totaux Gaz
  const totalAcheminementGazActuel = showAcheminement ? atrdActuel.totalHT : 0;
  const totalAcheminementGazFutur = showAcheminement ? atrdFutur.totalHT : 0;
  const totalAcciseGazActuel = showAccise ? acciseGazActuel : 0;
  const totalAcciseGazFutur = showAccise ? acciseGazFutur : 0;

  // ============================================
  // BUDGETS CONDITIONNELS (selon type d'énergie)
  // ============================================

  // Calculs Effacement Électrique (uniquement pour électricité)
  const tauxEffacement = parseFloat(effacement.tauxEffacement) / 100;
  const puissanceActive = puissanceSouscrite * 0.9; // kW
  const puissanceEffacableKW = puissanceActive * tauxEffacement;
  const puissanceEffacableMW = puissanceEffacableKW / 1000;
  
  // Gains effacement
  const gainCapacite = Math.round(puissanceEffacableMW * 30000);
  const volumeNEBEF = puissanceEffacableKW * 30 / 1000; // MWh
  const gainNEBEF = Math.round(volumeNEBEF * 250);
  const gainTotalEffacement = gainCapacite + gainNEBEF;
  const gainMin = Math.round(gainTotalEffacement * 0.85);
  const gainMax = Math.round(gainTotalEffacement * 1.15);
  
  // Validation puissance souscrite
  const isPuissanceValide = puissanceSouscrite >= 3 && puissanceSouscrite <= 10000;

  // Budget total conditionnel selon le type d'énergie
  let budgetActuel, budgetFutur, gainEffacementApplique, gainEnergyManager;
  
  if (energyType === "electricity") {
    // Budget Électricité
    budgetActuel = totalConsoActuel + totalAboPrestActuel + totalAcheminementActuel + totalAcciseActuel;
    // Gains effacement uniquement si NAF éligible (4-39), section ouverte et puissance valide
    gainEffacementApplique = (isNafEntre4et39 && showEffacement && isPuissanceValide && puissanceSouscrite > 0) ? gainTotalEffacement : 0;
    // Calcul du budget futur avant Energy Manager
    const budgetFuturAvantEM = totalConsoFutur + totalAboPrestFutur + totalAcheminementFutur + totalAcciseFutur - gainEffacementApplique;
    // Energy Manager : 5% du budget futur (consommation + abo + acheminement + accise)
    gainEnergyManager = showEnergyManager ? (totalConsoFutur + totalAboPrestFutur + totalAcheminementFutur + totalAcciseFutur) * 0.05 : 0;
    budgetFutur = budgetFuturAvantEM - gainEnergyManager;
  } else {
    // Budget Gaz
    budgetActuel = budgetConsoGazActuel + totalAboPrestActuel + totalAcheminementGazActuel + totalAcciseGazActuel;
    gainEffacementApplique = 0; // Pas d'effacement pour le gaz
    // Calcul du budget futur avant Energy Manager
    const budgetFuturAvantEM = budgetConsoGazFutur + totalAboPrestFutur + totalAcheminementGazFutur + totalAcciseGazFutur;
    // Energy Manager : 5% du budget futur
    gainEnergyManager = showEnergyManager ? (budgetConsoGazFutur + totalAboPrestFutur + totalAcheminementGazFutur + totalAcciseGazFutur) * 0.05 : 0;
    budgetFutur = budgetFuturAvantEM - gainEnergyManager;
  }
  
  const ecart = budgetFutur - budgetActuel;
  const ecartPourcent = budgetActuel > 0 ? (ecart / budgetActuel) * 100 : 0;

  // Mise à jour des postes
  const updatePoste = (id, field, value) => {
    setPostes(postes.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  // Mise à jour des postes Particulier
  const handlePosteParticulierChange = (id, field, value) => {
    setPostesParticulier(postesParticulier.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  // Calculs pour Particuliers - Électricité (uniquement sur les postes de l'offre courante)
  const totalConsoParticulierMWh = postesAffichesParticulier.reduce((sum, p) => sum + (parseFloat(p.conso) || 0), 0) / 1000; // kWh -> MWh pour cohérence
  const totalConsoParticulierKWh = postesAffichesParticulier.reduce((sum, p) => sum + (parseFloat(p.conso) || 0), 0);
  const totalParticulierActuel = postesAffichesParticulier.reduce((sum, p) => {
    const conso = parseFloat(p.conso) || 0;
    const prix = parseFloat(p.prixActuel) || 0;
    return sum + (conso * prix);
  }, 0) + (parseFloat(abonnementParticulier.prixActuel) || 0);
  
  const totalParticulierFutur = postesAffichesParticulier.reduce((sum, p) => {
    const conso = parseFloat(p.conso) || 0;
    const prix = parseFloat(p.prixFutur) || 0;
    return sum + (conso * prix);
  }, 0) + (parseFloat(abonnementParticulier.prixFutur) || 0);

  // Calculs pour Particuliers - Gaz
  const consoGazParticulierKWh = parseFloat(consoGazParticulier.conso) || 0;
  const totalGazParticulierActuel = (consoGazParticulierKWh * (parseFloat(consoGazParticulier.prixActuel) || 0)) + (parseFloat(abonnementGazParticulier.prixActuel) || 0);
  const totalGazParticulierFutur = (consoGazParticulierKWh * (parseFloat(consoGazParticulier.prixFutur) || 0)) + (parseFloat(abonnementGazParticulier.prixFutur) || 0);

  // Réinitialisation
  const handleReset = () => {
    setEnergyType("electricity");
    setClientName("");
    setCommercialName("");
    setNafCode("");
    setOptionTarifaire("");
    setSegmentElec("");
    setPuissanceSouscriteInput("");
    setPuissanceAtteinteInput("");
    setPostes(postes.map(p => ({ ...p, conso: "", prixActuel: "", prixFutur: "", prixCourtage: "" })));
    setTypeBudgetConso("actuel");
    setTypeBudgetAboPrest("actuel");
    // Gaz
    setSegmentGaz("T1");
    setCapaciteJournaliereT4("");
    setNiveauCapaciteT4("T4_low");
    setSegmentGazFutur("T1");
    setCapaciteJournaliereT4Futur("");
    setNiveauCapaciteT4Futur("T4_low");
    setConsoGaz({ conso: "", prixActuel: "", prixFutur: "" });
    setForceAcciseGazTauxPlein(false);
    // Commun
    setAbonnement({ quantite: 1, prixActuel: "", prixFutur: "", prixCourtage: "" });
    setPrestation({ quantite: 1, prixActuel: "", prixFutur: "", prixCourtage: "" });
    setAbonnementGaz({ quantite: 1, prixActuel: "", prixFutur: "", prixCourtage: "" });
    setPrestationGaz({ quantite: 1, prixActuel: "", prixFutur: "", prixCourtage: "" });
    setShowAcheminement(false);
    setAcheminement({ prixActuel: "", prixFutur: "" });
    setShowAccise(false);
    setAccise({ prixActuel: "", prixFutur: "" });
    setShowEffacement(false);
    setEffacement({ tauxEffacement: "20" });
    setForceTauxPlein(false);
    // Particulier
    setClientType("pro");
    setParticulierPriceDisplay("ht");
    setOffreParticulier("standard");
    setPostesParticulier(postesParticulier.map(p => ({ ...p, conso: "", prixActuel: "", prixFutur: "" })));
    setAbonnementParticulier({ prixActuel: "", prixFutur: "" });
    setConsoGazParticulier({ conso: "", prixActuel: "", prixFutur: "" });
    setAbonnementGazParticulier({ prixActuel: "", prixFutur: "" });
    setAdresseSite("");
    setPdlParticulier("");
    setFournisseurActuel("");
    setFournisseurFutur("");
  };

  // Fonction pour collecter toutes les données du formulaire
  const getCurrentProjectData = () => {
    return {
      energyType,
      clientName,
      commercialName,
      nafCode,
      optionTarifaire,
      segmentElec,
      puissanceSouscriteInput,
      puissanceAtteinteInput,
      postes,
      // Gaz
      segmentGaz,
      capaciteJournaliereT4,
      niveauCapaciteT4,
      segmentGazFutur,
      capaciteJournaliereT4Futur,
      niveauCapaciteT4Futur,
      consoGaz,
      forceAcciseGazTauxPlein,
      // Commun
      abonnement,
      prestation,
      abonnementGaz,
      prestationGaz,
      showAcheminement,
      acheminement,
      showAccise,
      accise,
      forceTauxPlein,
      showEffacement,
      effacement,
      // Particulier
      clientType,
      particulierPriceDisplay,
      offreParticulier,
      postesParticulier,
      abonnementParticulier,
      consoGazParticulier,
      abonnementGazParticulier,
      adresseSite,
      pdlParticulier,
      fournisseurActuel,
      fournisseurFutur,
    };
  };

  // Fonction pour charger les données d'un projet
  const loadProjectData = (data) => {
    if (!data) return;
    
    setEnergyType(data.energyType || "electricity");
    setClientName(data.clientName || "");
    setCommercialName(data.commercialName || "");
    setNafCode(data.nafCode || "");
    setOptionTarifaire(data.optionTarifaire || "");
    setSegmentElec(data.segmentElec || "");
    setPuissanceSouscriteInput(data.puissanceSouscriteInput || "");
    setPuissanceAtteinteInput(data.puissanceAtteinteInput || "");
    
    if (data.postes) {
      setPostes(data.postes);
    }
    
    // Gaz
    setSegmentGaz(data.segmentGaz || "T1");
    setCapaciteJournaliereT4(data.capaciteJournaliereT4 || "");
    setNiveauCapaciteT4(data.niveauCapaciteT4 || "T4_low");
    setSegmentGazFutur(data.segmentGazFutur || data.segmentGaz || "T1");
    setCapaciteJournaliereT4Futur(data.capaciteJournaliereT4Futur || data.capaciteJournaliereT4 || "");
    setNiveauCapaciteT4Futur(data.niveauCapaciteT4Futur || data.niveauCapaciteT4 || "T4_low");
    if (data.consoGaz) {
      setConsoGaz(data.consoGaz);
    }
    setForceAcciseGazTauxPlein(data.forceAcciseGazTauxPlein || false);
    
    if (data.abonnement) {
      setAbonnement(data.abonnement);
    }
    
    if (data.prestation) {
      setPrestation(data.prestation);
    }
    
    if (data.abonnementGaz) {
      setAbonnementGaz(data.abonnementGaz);
    }
    
    if (data.prestationGaz) {
      setPrestationGaz(data.prestationGaz);
    }
    
    setShowAcheminement(data.showAcheminement || false);
    if (data.acheminement) {
      setAcheminement(data.acheminement);
    }
    
    setShowAccise(data.showAccise || false);
    if (data.accise) {
      setAccise(data.accise);
    }
    setForceTauxPlein(data.forceTauxPlein || false);
    
    setShowEffacement(data.showEffacement || false);
    if (data.effacement) {
      setEffacement(data.effacement);
    }

    // Particulier
    if (data.clientType) setClientType(data.clientType);
    if (data.particulierPriceDisplay) setParticulierPriceDisplay(data.particulierPriceDisplay);
    if (data.offreParticulier) setOffreParticulier(data.offreParticulier);
    if (data.postesParticulier) setPostesParticulier(data.postesParticulier);
    if (data.abonnementParticulier) setAbonnementParticulier(data.abonnementParticulier);
    if (data.consoGazParticulier) setConsoGazParticulier(data.consoGazParticulier);
    if (data.abonnementGazParticulier) setAbonnementGazParticulier(data.abonnementGazParticulier);
    if (data.adresseSite !== undefined) setAdresseSite(data.adresseSite);
    if (data.pdlParticulier !== undefined) setPdlParticulier(data.pdlParticulier);
    if (data.fournisseurActuel !== undefined) setFournisseurActuel(data.fournisseurActuel);
    if (data.fournisseurFutur !== undefined) setFournisseurFutur(data.fournisseurFutur);
  };

  // Fonction pour tracker l'export PDF
  const trackPdfExport = async () => {
    if (isAuthenticated && token) {
      try {
        await fetch(`${API_URL}/api/exports/track`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (error) {
        logger.error('Error tracking export:', error);
      }
    }
  };

  // Export PDF PRO avec charte graphique Place des Énergies
  const handleExportPDF = () => {
    try {
      _doExportPDF();
    } catch (err) {
      logger.error('PDF export Pro failed:', err);
      alert(`Erreur lors de la génération du PDF : ${err?.message || err}\n\nMerci d'envoyer une capture d'écran à votre référent technique.`);
    }
  };

  const _doExportPDF = () => {
    // Track export
    trackPdfExport();
    
    // Créer le PDF avec compression maximale
    const doc = new jsPDF({
      compress: true,
      putOnlyUsedFonts: true
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Couleurs charte graphique Place des Énergies
    const AZUR = [57, 125, 255]; // #397DFF - Bleu principal
    const ANTHRACITE = [25, 29, 30]; // #191D1E - Texte principal
    const BLANC_LUNAIRE = [245, 246, 249]; // #F5F6F9
    const EMERAUDE = [16, 209, 131]; // #10D183 - Gains/économies
    const AMBRE = [249, 200, 14]; // #F9C80E - Alertes
    const GRIS_ACIER = [147, 155, 160]; // #939BA0
    
    // Couleurs selon le type d'énergie
    const primaryColor = energyType === 'electricity' ? AZUR : [249, 115, 22]; // Azur ou Orange
    const darkColor = ANTHRACITE;
    
    // Fonctions de formatage compactes
    const fmt = (num) => num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const fmtEUR = (num) => fmt(num) + ' €';
    // Prix unitaire : 4 décimales (€/MWh, €/kWh) — ne pas arrondir les tarifs commerciaux.
    const fmtPrice = (num) => {
      const truncated = Number(num || 0).toFixed(4);
      // Trim trailing zeros after decimal for readability (e.g. "56.7800" → "56,78", but "56.7821" stays)
      const [intPart, decPart] = truncated.split('.');
      const trimmed = decPart.replace(/0+$/, '').padEnd(2, '0');
      return `${intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${trimmed}`;
    };
    const fmtEURAn = (num) => fmt(num) + ' €/an';
    
    // ============================================
    // EN-TÊTE PROFESSIONNEL
    // ============================================
    // Bandeau supérieur
    doc.setFillColor(...ANTHRACITE);
    doc.rect(0, 0, pageWidth, 8, 'F');
    
    // Zone blanche avec logo
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 8, pageWidth, 32, 'F');
    
    // Logo PDE haute résolution (PNG 2400×956 px, ratio 2.51, ~ 1100 DPI sur 55mm)
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'PNG', 12, 12, 55, 21.9, undefined, 'SLOW');
      } catch (e) {
        logger.error("Logo error:", e);
      }
    }
    
    // Titre principal à droite
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...ANTHRACITE);
    doc.text("COMPARATIF BUDGET", pageWidth - 12, 22, { align: 'right' });
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRIS_ACIER);
    doc.text(energyType === 'electricity' ? "Électricité" : "Gaz Naturel", pageWidth - 12, 30, { align: 'right' });
    
    // Ligne de séparation colorée
    doc.setFillColor(...primaryColor);
    doc.rect(0, 40, pageWidth, 3, 'F');
    
    // Infos client et commercial
    let yPos = 52;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...ANTHRACITE);
    const date = new Date().toLocaleDateString('fr-FR');
    doc.text(`Date : ${date}`, 12, yPos);
    
    if (clientName) {
      doc.text(`Client : ${clientName}`, 80, yPos);
    }
    if (commercialName) {
      doc.text(`Commercial : ${commercialName}`, 150, yPos);
    }
    
    yPos += 8;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...GRIS_ACIER);
    
    // Infos techniques
    let infoLine = '';
    if (energyType === 'electricity') {
      if (puissanceSouscrite > 0) infoLine += `P.souscrite: ${puissanceSouscrite.toFixed(0)} kVA`;
      const puissanceAtteinte = parseFloat(puissanceAtteinteInput) || 0;
      if (puissanceAtteinte > 0) infoLine += ` | P.atteinte: ${puissanceAtteinte.toFixed(0)} kVA`;
      if (nafCode) infoLine += ` | NAF: ${nafCode}`;
      if (optionTarifaire) infoLine += ` | Option: ${optionTarifaire}`;
    } else {
      infoLine = `Segment: ${segmentGaz}`;
      if (nafCode) infoLine += ` | NAF: ${nafCode} (${isNafIndustrielGaz ? 'Industriel' : 'Standard'})`;
      if (segmentGaz === 'T4' && capaciteT4 > 0) infoLine += ` | Capacité: ${capaciteT4} MWh/j`;
    }
    if (infoLine) doc.text(infoLine, 12, yPos);
    
    yPos += 10;
    
    // ============================================
    // SECTION CONSOMMATION
    // ============================================
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text(energyType === 'electricity' ? "CONSOMMATION PAR POSTE" : "CONSOMMATION GAZ", 12, yPos);
    yPos += 5;
    
    let consoData, consoTotalMWh, consoTotalActuel, consoTotalFutur, prixMoyAct, prixMoyFut;
    
    if (energyType === 'electricity') {
      consoData = postes.map(p => [
        p.label,
        p.conso || "-",
        p.prixActuel || "-",
        fmtEUR(calculateBudget(p.conso, p.prixActuel)),
        p.prixFutur || "-",
        fmtEUR(calculateBudget(p.conso, p.prixFutur))
      ]);
      consoTotalMWh = totalConsoMWh;
      consoTotalActuel = totalConsoActuel;
      consoTotalFutur = totalConsoFutur;
      prixMoyAct = prixMoyenActuel;
      prixMoyFut = prixMoyenFutur;
    } else {
      consoData = [[
        "Part Conso",
        consoGaz.conso || "-",
        consoGaz.prixActuel || "-",
        fmtEUR(budgetConsoGazActuel),
        consoGaz.prixFutur || "-",
        fmtEUR(budgetConsoGazFutur)
      ]];
      consoTotalMWh = consoGazMWh;
      consoTotalActuel = budgetConsoGazActuel;
      consoTotalFutur = budgetConsoGazFutur;
      prixMoyAct = prixMoyenGazActuel;
      prixMoyFut = prixMoyenGazFutur;
    }
    
    // Ligne total
    consoData.push([
      { content: 'TOTAL', styles: { fontStyle: 'bold' } },
      { content: fmt(consoTotalMWh) + ' MWh', styles: { fontStyle: 'bold' } },
      { content: fmtPrice(prixMoyAct) + ' €/MWh', styles: { fontStyle: 'bold', textColor: primaryColor } },
      { content: fmtEUR(consoTotalActuel), styles: { fontStyle: 'bold' } },
      { content: fmtPrice(prixMoyFut) + ' €/MWh', styles: { fontStyle: 'bold', textColor: primaryColor } },
      { content: fmtEUR(consoTotalFutur), styles: { fontStyle: 'bold' } }
    ]);
    
    doc.autoTable({
      startY: yPos,
      head: [['Poste', 'MWh', 'Prix Act.', 'Budget Act.', 'Prix Fut.', 'Budget Fut.']],
      body: consoData,
      theme: 'striped',
      headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 8, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 2.5 },
      alternateRowStyles: { fillColor: BLANC_LUNAIRE },
      columnStyles: { 0: { fontStyle: 'bold' }, 3: { halign: 'right' }, 5: { halign: 'right' } }
    });
    
    yPos = doc.lastAutoTable.finalY + 6;
    
    // Alerte audit si conso >= 2750 MWh
    if (consoTotalMWh >= 2750) {
      doc.setFillColor(254, 243, 199);
      doc.roundedRect(12, yPos, pageWidth - 24, 14, 2, 2, 'F');
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(180, 83, 9);
      doc.text("AUDIT ENERGETIQUE OBLIGATOIRE - Conso " + fmt(consoTotalMWh) + " MWh (>2750) - Amende: 75 000 EUR", pageWidth / 2, yPos + 9, { align: 'center' });
      yPos += 18;
    }
    
    // ============================================
    // ABONNEMENT & PRESTATIONS
    // ============================================
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text("Abonnement & Prestations", 12, yPos);
    yPos += 4;
    
    const aboData = [
      ['Abonnement', currentAbonnement.quantite, currentAbonnement.prixActuel || "-", fmtEURAn((parseFloat(currentAbonnement.prixActuel) || 0) * currentAbonnement.quantite), currentAbonnement.prixFutur || "-", fmtEURAn((parseFloat(currentAbonnement.prixFutur) || 0) * currentAbonnement.quantite)],
      ['Prestation', currentPrestation.quantite, currentPrestation.prixActuel || "-", fmtEURAn((parseFloat(currentPrestation.prixActuel) || 0) * currentPrestation.quantite), currentPrestation.prixFutur || "-", fmtEURAn((parseFloat(currentPrestation.prixFutur) || 0) * currentPrestation.quantite)],
      [{ content: 'TOTAL', styles: { fontStyle: 'bold' } }, '', '', { content: fmtEURAn(totalAboPrestActuel), styles: { fontStyle: 'bold' } }, '', { content: fmtEURAn(totalAboPrestFutur), styles: { fontStyle: 'bold' } }]
    ];
    
    doc.autoTable({
      startY: yPos,
      head: [['Type', 'Qté', 'Prix Act.', 'Budget Act.', 'Prix Fut.', 'Budget Fut.']],
      body: aboData,
      theme: 'striped',
      headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 8 },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: 'bold' }, 3: { halign: 'right' }, 5: { halign: 'right' } }
    });
    
    yPos = doc.lastAutoTable.finalY + 8;
    
    // ============================================
    // ACHEMINEMENT (selon type d'énergie)
    // ============================================
    if (showAcheminement) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...primaryColor);
      
      if (energyType === 'electricity' && optionTarifaire && puissanceSouscrite > 0) {
        doc.text("Acheminement TURPE + CTA", 12, yPos);
        yPos += 4;
        
        const achData = [
          segmentElec ? ['Segment client', SEGMENTS_ELEC[segmentElec]?.label || segmentElec, ''] : null,
          ['Option tarifaire', optionTarifaire, ''],
          ['TURPE HT', fmtEUR(acheminementActuelCalc.turpeHT), fmtEUR(acheminementFuturCalc.turpeHT)],
          ['CTA (15%)', fmtEUR(acheminementActuelCalc.cta), fmtEUR(acheminementFuturCalc.cta)],
          [{ content: 'TOTAL HT', styles: { fontStyle: 'bold' } }, { content: fmtEURAn(acheminementActuelCalc.total), styles: { fontStyle: 'bold' } }, { content: fmtEURAn(acheminementFuturCalc.total), styles: { fontStyle: 'bold' } }]
        ].filter(Boolean);
        
        doc.autoTable({
          startY: yPos,
          head: [['Element', 'Budget Actuel', 'Budget Futur']],
          body: achData,
          theme: 'striped',
          headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 8 },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } }
        });
        
      } else if (energyType === 'gas') {
        doc.text("Acheminement Gaz ATRD + CTA (Comparatif Segments)", 12, yPos);
        yPos += 4;
        
        // Comparatif des deux segments
        const atrdData = [
          ['Segment', segmentGaz, segmentGazFutur],
          ['Abonnement ATRD', fmtEUR(ATRD.abonnement[segmentGaz]), fmtEUR(ATRD.abonnement[segmentGazFutur])],
          ['Coeff. variable', fmt(ATRD.variable[segmentGaz]) + ' EUR/MWh', fmt(ATRD.variable[segmentGazFutur]) + ' EUR/MWh'],
          ['Part variable', fmtEUR(atrdActuel.partVariable), fmtEUR(atrdFutur.partVariable)],
          ...(segmentGaz === 'T4' || segmentGazFutur === 'T4' ? [['Part capacite', fmtEUR(atrdActuel.partCapacite), fmtEUR(atrdFutur.partCapacite)]] : []),
          ['ATRD HT', fmtEUR(atrdActuel.atrdHT), fmtEUR(atrdFutur.atrdHT)],
          ['CTA (20,80%)', fmtEUR(atrdActuel.cta), fmtEUR(atrdFutur.cta)],
          [{ content: 'TOTAL ATRD+CTA', styles: { fontStyle: 'bold' } }, { content: fmtEURAn(atrdActuel.totalHT), styles: { fontStyle: 'bold' } }, { content: fmtEURAn(atrdFutur.totalHT), styles: { fontStyle: 'bold' } }]
        ];
        
        // Ajouter la ligne d'écart si les segments sont différents
        if (segmentGaz !== segmentGazFutur) {
          const ecartATRD = atrdFutur.totalHT - atrdActuel.totalHT;
          atrdData.push([
            { content: 'ECART', styles: { fontStyle: 'bold', textColor: ecartATRD < 0 ? [34, 197, 94] : [239, 68, 68] } },
            '',
            { content: (ecartATRD < 0 ? '' : '+') + fmtEURAn(ecartATRD), styles: { fontStyle: 'bold', textColor: ecartATRD < 0 ? [34, 197, 94] : [239, 68, 68] } }
          ]);
        }
        
        doc.autoTable({
          startY: yPos,
          head: [['Element', 'Actuel (' + segmentGaz + ')', 'Futur (' + segmentGazFutur + ')']],
          body: atrdData,
          theme: 'striped',
          headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 8 },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } }
        });
      }
      
      yPos = doc.lastAutoTable.finalY + 8;
    }
    
    // ============================================
    // ACCISE (selon type d'énergie)
    // ============================================
    if (showAccise) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...primaryColor);
      
      if (energyType === 'electricity' && totalConsoMWh > 0) {
        doc.text("Accise sur l'Electricite", 12, yPos);
        yPos += 4;
        
        const accData = [
          ['Consommation', fmt(totalConsoMWh) + ' MWh', ''],
          ['Taux actuel', fmt(tauxAccise) + ' EUR/MWh', fmtEURAn(acciseAutoActuel)],
          ['Taux futur', fmt(tauxAcciseFutur) + ' EUR/MWh' + (isNafEntre4et39 && !forceTauxPlein ? ' (reduit)' : ''), fmtEURAn(acciseAutoFutur)],
          [{ content: 'TOTAL ACCISE', styles: { fontStyle: 'bold' } }, '', { content: 'Act: ' + fmtEURAn(acciseAutoActuel) + ' | Fut: ' + fmtEURAn(acciseAutoFutur), styles: { fontStyle: 'bold' } }]
        ];
        
        doc.autoTable({
          startY: yPos,
          head: [['Element', 'Valeur', 'Montant']],
          body: accData,
          theme: 'striped',
          headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 8 },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: { 2: { halign: 'right' } }
        });
        
      } else if (energyType === 'gas' && consoGazMWh > 0) {
        doc.text("Accise Gaz (TICGN)", 12, yPos);
        yPos += 4;
        
        const ticgnData = [
          ['Consommation gaz', fmt(consoGazMWh) + ' MWh', ''],
          ['Code NAF', nafCode || 'Non renseigne', isNafIndustrielGaz ? '(Industriel B/C/D)' : '(Standard)'],
          ['Taux actuel', fmt(tauxAcciseGazActuel) + ' EUR/MWh', fmtEURAn(acciseGazActuel)],
          ['Taux futur', fmt(tauxAcciseGazFutur) + ' EUR/MWh' + (isNafIndustrielGaz && !forceAcciseGazTauxPlein ? ' (reduit)' : ''), fmtEURAn(acciseGazFutur)],
          [{ content: 'ACCISE GAZ', styles: { fontStyle: 'bold' } }, { content: 'Actuel: ' + fmtEURAn(acciseGazActuel), styles: { fontStyle: 'bold' } }, { content: 'Futur: ' + fmtEURAn(acciseGazFutur), styles: { fontStyle: 'bold' } }]
        ];
        
        doc.autoTable({
          startY: yPos,
          head: [['Element', 'Valeur', 'Montant']],
          body: ticgnData,
          theme: 'striped',
          headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 8 },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: { 2: { halign: 'right' } }
        });
      }
      
      yPos = doc.lastAutoTable.finalY + 8;
    }
    
    // ============================================
    // EFFACEMENT ÉLECTRIQUE (uniquement électricité et NAF éligible 4-39)
    // ============================================
    if (energyType === 'electricity' && isNafEntre4et39 && showEffacement && isPuissanceValide && puissanceSouscrite > 0) {
      if (yPos + 50 > pageHeight) { doc.addPage(); yPos = 15; }
      
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...primaryColor);
      doc.text("Estimation Effacement Electrique", 12, yPos);
      yPos += 4;
      
      const effData = [
        ['Puissance souscrite', puissanceSouscrite.toFixed(0) + ' kVA', ''],
        ['Taux effacement', effacement.tauxEffacement + ' %', ''],
        ['Puissance effacable', puissanceEffacableKW.toFixed(0) + ' kW', ''],
        ['Gain Capacite', '30 000 EUR/MW/an', fmtEUR(gainCapacite)],
        ['Gain NEBEF', '30h x 250 EUR/MWh', fmtEUR(gainNEBEF)],
        [{ content: 'GAIN TOTAL', styles: { fontStyle: 'bold', textColor: [34, 197, 94] } }, '', { content: fmtEURAn(gainTotalEffacement), styles: { fontStyle: 'bold', textColor: [34, 197, 94] } }]
      ];
      
      doc.autoTable({
        startY: yPos,
        head: [['Parametre', 'Hypothese', 'Montant']],
        body: effData,
        theme: 'striped',
        headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 8 },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: { 2: { halign: 'right' } }
      });
      
      yPos = doc.lastAutoTable.finalY + 8;
    }
    
    // ============================================
    // COMPARATIF FINAL
    // ============================================
    if (yPos + 60 > pageHeight) { doc.addPage(); yPos = 15; }
    
    const boxHeight = 50;
    
    // Fond du comparatif
    doc.setFillColor(...BLANC_LUNAIRE);
    doc.roundedRect(12, yPos, pageWidth - 24, boxHeight, 4, 4, 'F');
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(1);
    doc.roundedRect(12, yPos, pageWidth - 24, boxHeight, 4, 4, 'S');
    
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...ANTHRACITE);
    doc.text("RÉCAPITULATIF BUDGET " + (energyType === 'electricity' ? "ÉLECTRICITÉ" : "GAZ"), pageWidth / 2, yPos + 12, { align: 'center' });
    
    const colW = (pageWidth - 24) / 3;
    
    // Budget Actuel
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRIS_ACIER);
    doc.text("Budget Actuel", 12 + colW / 2, yPos + 24, { align: 'center' });
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...ANTHRACITE);
    doc.text(fmtEUR(budgetActuel), 12 + colW / 2, yPos + 36, { align: 'center' });
    
    // Budget Futur
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRIS_ACIER);
    const futLabel = gainEffacementApplique > 0 ? "Budget Futur (- effacement)" : "Budget Futur";
    doc.text(futLabel, 12 + colW + colW / 2, yPos + 24, { align: 'center' });
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text(fmtEUR(budgetFutur), 12 + colW + colW / 2, yPos + 36, { align: 'center' });
    
    // Écart
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRIS_ACIER);
    doc.text("Écart", 12 + 2 * colW + colW / 2, yPos + 24, { align: 'center' });
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    // Couleurs : Vert émeraude si économie, rouge si surcoût
    let ecartColor = ANTHRACITE;
    let sign = "";
    if (ecart > 0) {
      ecartColor = [220, 38, 38];
      sign = "+";
    } else if (ecart < 0) {
      ecartColor = EMERAUDE;
    }
    doc.setTextColor(...ecartColor);
    doc.text(sign + fmtEUR(ecart), 12 + 2 * colW + colW / 2, yPos + 34, { align: 'center' });
    doc.setFontSize(10);
    doc.text('(' + sign + ecartPourcent.toFixed(1) + '%)', 12 + 2 * colW + colW / 2, yPos + 42, { align: 'center' });
    
    yPos += boxHeight + 8;
    
    // Disclaimer
    doc.setFillColor(254, 243, 199);
    doc.roundedRect(12, yPos, pageWidth - 24, 12, 2, 2, 'F');
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...AMBRE);
    doc.text("⚠ Chiffres estimatifs - À valider par une étude approfondie", pageWidth / 2, yPos + 8, { align: 'center' });
    
    // ============================================
    // PIED DE PAGE PROFESSIONNEL
    // ============================================
    // Bandeau bas
    doc.setFillColor(...ANTHRACITE);
    doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
    
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(255, 255, 255);
    doc.text("Place des Énergies - Votre partenaire en intelligence énergétique", 12, pageHeight - 6);
    doc.text("www.placedesenergies.com", pageWidth - 12, pageHeight - 6, { align: 'right' });
    
    // Nom du fichier
    const typeLabel = energyType === 'electricity' ? 'elec' : 'gaz';
    const fileName = clientName ? `budget_${typeLabel}_${clientName.replace(/\s+/g, '_')}.pdf` : `budget_${typeLabel}.pdf`;
    doc.save(fileName);
  };
  
  // ============================================
  // Export PDF PARTICULIER
  // ============================================
  const handleExportPDFParticulier = () => {
    try {
      _doExportPDFParticulier();
    } catch (err) {
      logger.error('PDF export Particulier failed:', err);
      alert(`Erreur lors de la génération du PDF Particulier : ${err?.message || err}\n\nMerci d'envoyer une capture d'écran à votre référent technique.`);
    }
  };

  const _doExportPDFParticulier = () => {
    trackPdfExport();
    
    const doc = new jsPDF({
      compress: true,
      putOnlyUsedFonts: true
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Couleurs charte graphique Place des Énergies
    const AZUR = [57, 125, 255];
    const ANTHRACITE = [25, 29, 30];
    const BLANC_LUNAIRE = [245, 246, 249];
    const EMERAUDE = [16, 209, 131];
    const AMBRE = [249, 200, 14];
    const GRIS_ACIER = [147, 155, 160];
    const VIOLET = [139, 92, 246]; // Pour particuliers
    
    const primaryColor = energyType === 'electricity' ? AZUR : [249, 115, 22];
    
    const fmt = (num) => num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const fmtEUR = (num) => fmt(num) + ' €';
    // Prix unitaire : 4 décimales (€/MWh, €/kWh) — ne pas arrondir les tarifs commerciaux.
    const fmtPrice = (num) => {
      const truncated = Number(num || 0).toFixed(4);
      // Trim trailing zeros after decimal for readability (e.g. "56.7800" → "56,78", but "56.7821" stays)
      const [intPart, decPart] = truncated.split('.');
      const trimmed = decPart.replace(/0+$/, '').padEnd(2, '0');
      return `${intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${trimmed}`;
    };
    
    // ============================================
    // EN-TÊTE
    // ============================================
    doc.setFillColor(...ANTHRACITE);
    doc.rect(0, 0, pageWidth, 8, 'F');
    
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 8, pageWidth, 32, 'F');
    
    // Logo PDE haute résolution (PNG 2400×956 px, ratio 2.51, ~ 1100 DPI sur 55mm)
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'PNG', 12, 12, 55, 21.9, undefined, 'SLOW');
      } catch (e) {
        logger.error("Logo error:", e);
      }
    }
    
    // Badge Particulier
    doc.setFillColor(...VIOLET);
    doc.roundedRect(pageWidth - 55, 12, 43, 10, 2, 2, 'F');
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("PARTICULIER", pageWidth - 33.5, 19, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...ANTHRACITE);
    doc.text("COMPARATIF BUDGET", pageWidth - 12, 35, { align: 'right' });
    
    doc.setFillColor(...primaryColor);
    doc.rect(0, 40, pageWidth, 3, 'F');
    
    // Infos client
    let yPos = 52;
    const date = new Date().toLocaleDateString('fr-FR');
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...ANTHRACITE);
    doc.text(`Date : ${date}`, 12, yPos);
    if (clientName) {
      doc.text(`Client : ${clientName}`, 80, yPos);
    }

    // Adresse + PDL (nouvelle ligne)
    if (adresseSite || pdlParticulier) {
      yPos += 5;
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...ANTHRACITE);
      if (adresseSite) doc.text(`Site : ${adresseSite}`, 12, yPos);
      if (pdlParticulier) doc.text(`PDL : ${pdlParticulier}`, pageWidth - 12, yPos, { align: 'right' });
    }

    yPos += 6;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRIS_ACIER);
    const offreLabel = energyType === 'electricity'
      ? ({ standard: 'Standard', tempo: 'Tempo', zen: 'Zen Week-End' }[offreParticulier] || 'Standard')
      : 'Gaz';
    doc.text(`Type d'énergie : ${energyType === 'electricity' ? 'Électricité' : 'Gaz'} | Offre : ${offreLabel} | Affichage : ${particulierPriceDisplay === 'ht' ? 'HT' : 'TTC'}`, 12, yPos);

    yPos += 12;
    
    // ============================================
    // TABLEAU CONSOMMATION PARTICULIER
    // ============================================
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text(`CONSOMMATION ${energyType === 'electricity' ? 'ÉLECTRICITÉ' : 'GAZ'} (${particulierPriceDisplay.toUpperCase()})`, 12, yPos);
    yPos += 5;
    
    let tableData = [];
    let totalActuel, totalFutur, totalKWh;
    
    if (energyType === 'electricity') {
      postesAffichesParticulier.forEach(p => {
        const conso = parseFloat(p.conso) || 0;
        const prixAct = parseFloat(p.prixActuel) || 0;
        const prixFut = parseFloat(p.prixFutur) || 0;
        tableData.push([
          p.label,
          conso > 0 ? `${conso} kWh` : '-',
          prixAct > 0 ? `${prixAct.toFixed(4)} €/kWh` : '-',
          fmtEUR(conso * prixAct),
          prixFut > 0 ? `${prixFut.toFixed(4)} €/kWh` : '-',
          fmtEUR(conso * prixFut)
        ]);
      });
      // Abonnement
      const aboAct = parseFloat(abonnementParticulier.prixActuel) || 0;
      const aboFut = parseFloat(abonnementParticulier.prixFutur) || 0;
      tableData.push([
        { content: 'Abonnement annuel', styles: { fontStyle: 'bold' } },
        '-',
        aboAct > 0 ? `${fmtEUR(aboAct)}/an` : '-',
        fmtEUR(aboAct),
        aboFut > 0 ? `${fmtEUR(aboFut)}/an` : '-',
        fmtEUR(aboFut)
      ]);
      
      totalActuel = totalParticulierActuel;
      totalFutur = totalParticulierFutur;
      totalKWh = totalConsoParticulierKWh;
    } else {
      const conso = parseFloat(consoGazParticulier.conso) || 0;
      const prixAct = parseFloat(consoGazParticulier.prixActuel) || 0;
      const prixFut = parseFloat(consoGazParticulier.prixFutur) || 0;
      tableData.push([
        'Consommation Gaz',
        conso > 0 ? `${conso} kWh` : '-',
        prixAct > 0 ? `${prixAct.toFixed(4)} €/kWh` : '-',
        fmtEUR(conso * prixAct),
        prixFut > 0 ? `${prixFut.toFixed(4)} €/kWh` : '-',
        fmtEUR(conso * prixFut)
      ]);
      // Abonnement Gaz
      const aboAct = parseFloat(abonnementGazParticulier.prixActuel) || 0;
      const aboFut = parseFloat(abonnementGazParticulier.prixFutur) || 0;
      tableData.push([
        { content: 'Abonnement annuel', styles: { fontStyle: 'bold' } },
        '-',
        aboAct > 0 ? `${fmtEUR(aboAct)}/an` : '-',
        fmtEUR(aboAct),
        aboFut > 0 ? `${fmtEUR(aboFut)}/an` : '-',
        fmtEUR(aboFut)
      ]);
      
      totalActuel = totalGazParticulierActuel;
      totalFutur = totalGazParticulierFutur;
      totalKWh = consoGazParticulierKWh;
    }
    
    // Ligne total
    tableData.push([
      { content: `TOTAL ${particulierPriceDisplay.toUpperCase()}`, styles: { fontStyle: 'bold', fillColor: primaryColor, textColor: [255, 255, 255] } },
      { content: `${totalKWh} kWh`, styles: { fontStyle: 'bold', fillColor: primaryColor, textColor: [255, 255, 255] } },
      { content: '', styles: { fillColor: primaryColor } },
      { content: fmtEUR(totalActuel), styles: { fontStyle: 'bold', fillColor: primaryColor, textColor: [255, 255, 255] } },
      { content: '', styles: { fillColor: primaryColor } },
      { content: fmtEUR(totalFutur), styles: { fontStyle: 'bold', fillColor: primaryColor, textColor: [255, 255, 255] } }
    ]);
    
    // En-tête : 2 lignes — fournisseurs au-dessus, libellés en dessous
    const fournisseurActuelLabel = fournisseurActuel ? `Fournisseur : ${fournisseurActuel}` : 'Fournisseur actuel';
    const fournisseurFuturLabel = fournisseurFutur ? `Fournisseur : ${fournisseurFutur}` : 'Fournisseur futur';
    const fournisseurFillActuel = [241, 245, 249];   // gris doux
    const fournisseurFillFutur = [236, 253, 245];    // vert doux

    doc.autoTable({
      startY: yPos,
      head: [
        [
          { content: '', colSpan: 2, styles: { fillColor: [255, 255, 255], textColor: 0 } },
          { content: fournisseurActuelLabel, colSpan: 2, styles: { fillColor: fournisseurFillActuel, textColor: ANTHRACITE, fontStyle: 'bold', halign: 'center', fontSize: 9 } },
          { content: fournisseurFuturLabel, colSpan: 2, styles: { fillColor: fournisseurFillFutur, textColor: [22, 101, 52], fontStyle: 'bold', halign: 'center', fontSize: 9 } },
        ],
        ['Poste', 'Conso', 'Prix Act.', 'Total Act.', 'Prix Fut.', 'Total Fut.'],
      ],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 9, fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 3 },
      alternateRowStyles: { fillColor: BLANC_LUNAIRE },
      columnStyles: { 0: { fontStyle: 'bold' }, 3: { halign: 'right' }, 5: { halign: 'right' } }
    });
    
    yPos = doc.lastAutoTable.finalY + 15;
    
    // ============================================
    // COMPARATIF VISUEL
    // ============================================
    const boxHeight = 55;
    doc.setFillColor(...BLANC_LUNAIRE);
    doc.roundedRect(12, yPos, pageWidth - 24, boxHeight, 4, 4, 'F');
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(1);
    doc.roundedRect(12, yPos, pageWidth - 24, boxHeight, 4, 4, 'S');
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...ANTHRACITE);
    doc.text(`COMPARATIF BUDGET ${particulierPriceDisplay.toUpperCase()}`, pageWidth / 2, yPos + 12, { align: 'center' });
    
    const colW = (pageWidth - 24) / 3;
    
    // Budget Actuel
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...ANTHRACITE);
    if (fournisseurActuel) {
      doc.text(`Fournisseur : ${fournisseurActuel}`, 12 + colW / 2, yPos + 21, { align: 'center' });
    }
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRIS_ACIER);
    doc.text(`Budget Actuel ${particulierPriceDisplay.toUpperCase()}`, 12 + colW / 2, yPos + 28, { align: 'center' });
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...ANTHRACITE);
    doc.text(fmtEUR(totalActuel) + '/an', 12 + colW / 2, yPos + 42, { align: 'center' });

    // Budget Futur
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    if (fournisseurFutur) {
      doc.text(`Fournisseur : ${fournisseurFutur}`, 12 + colW + colW / 2, yPos + 21, { align: 'center' });
    }
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRIS_ACIER);
    doc.text(`Budget Futur ${particulierPriceDisplay.toUpperCase()}`, 12 + colW + colW / 2, yPos + 28, { align: 'center' });
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text(fmtEUR(totalFutur) + '/an', 12 + colW + colW / 2, yPos + 42, { align: 'center' });
    
    // Écart
    const diff = totalFutur - totalActuel;
    const pct = totalActuel > 0 ? ((diff / totalActuel) * 100) : 0;
    let ecartColor = ANTHRACITE;
    if (diff > 0) ecartColor = [220, 38, 38];
    else if (diff < 0) ecartColor = EMERAUDE;
    
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRIS_ACIER);
    doc.text("Économie / Surcoût", 12 + 2 * colW + colW / 2, yPos + 26, { align: 'center' });
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...ecartColor);
    const signDiff = diff > 0 ? '+' : '';
    doc.text(signDiff + fmtEUR(diff) + '/an', 12 + 2 * colW + colW / 2, yPos + 38, { align: 'center' });
    doc.setFontSize(10);
    doc.text('(' + signDiff + pct.toFixed(1) + '%)', 12 + 2 * colW + colW / 2, yPos + 48, { align: 'center' });
    
    // ============================================
    // PIED DE PAGE
    // ============================================
    doc.setFillColor(...ANTHRACITE);
    doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
    
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(255, 255, 255);
    doc.text("Place des Énergies - Votre partenaire en intelligence énergétique", 12, pageHeight - 6);
    doc.text("www.placedesenergies.com", pageWidth - 12, pageHeight - 6, { align: 'right' });
    
    const typeLabel = energyType === 'electricity' ? 'elec' : 'gaz';
    const fileName = clientName ? `budget_particulier_${typeLabel}_${clientName.replace(/\s+/g, '_')}.pdf` : `budget_particulier_${typeLabel}.pdf`;
    doc.save(fileName);
  };

  // Formatage des nombres
  const formatNumber = (num) => {
    return num.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Format dédié pour les prix unitaires (€/MWh, €/kWh) :
  // 4 décimales pour ne pas arrondir les tarifs commerciaux (ex: 56,4321 €/MWh).
  const formatPrice = (num) => {
    return Number(num || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  };

  // Si l'utilisateur n'est pas authentifié, afficher la page de connexion
  if (!isAuthenticated) {
    return (
      <div className="app-container auth-required" data-testid="app-container">
        <AuthPage 
          onClose={() => {}} 
          isRequired={true}
        />
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="app-container">
      {/* Projects Panel Modal */}
      {showProjectsPanel && (
        <ProjectsPanel 
          onClose={() => setShowProjectsPanel(false)}
          onLoadProject={loadProjectData}
          currentData={getCurrentProjectData()}
          onSaveSuccess={() => {}}
        />
      )}

      {/* User Dashboard Modal */}
      {showUserDashboard && (
        <UserDashboard onClose={() => setShowUserDashboard(false)} />
      )}

      {/* Header */}
      <header className="header" data-testid="header">
        <div className="header-content">
          <div className="logo-section">
            <img 
              src={LOGO_URL} 
              alt="Place des Énergies" 
              className="logo-image"
              data-testid="logo-image"
            />
          </div>
          <div className="header-actions">
            {isAdmin && (
              <button 
                onClick={() => navigate('/admin')} 
                className="btn btn-admin"
                data-testid="admin-btn"
              >
                <Shield size={18} />
                Admin
              </button>
            )}
            <button 
              onClick={() => setShowUserDashboard(true)} 
              className="btn btn-dashboard"
              data-testid="dashboard-btn"
            >
              <BarChart2 size={18} />
              Mon Tableau de Bord
            </button>
            <button 
              onClick={() => setShowProjectsPanel(true)} 
              className="btn btn-projects"
              data-testid="projects-btn"
            >
              <FolderOpen size={18} />
              Mes projets
            </button>
            <div className="user-info-header">
              <User size={16} />
              <span>{user?.name}</span>
            </div>
            <button onClick={logout} className="btn btn-secondary" data-testid="logout-btn">
              <LogOut size={18} />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="main-content" ref={formRef}>
        {/* Sélecteur Type Client (Pro/Particulier) */}
        <ClientTypeSelector clientType={clientType} setClientType={setClientType} />

        {/* Sélecteur Type d'Énergie */}
        <EnergyTypeSelector energyType={energyType} setEnergyType={setEnergyType} />

        {/* SECTION PARTICULIER - Infos client + Consommation simplifiée */}
        {clientType === 'particulier' && (
          <ParticulierSection
            energyType={energyType}
            particulierPriceDisplay={particulierPriceDisplay}
            setParticulierPriceDisplay={setParticulierPriceDisplay}
            clientName={clientName}
            setClientName={setClientName}
            adresseSite={adresseSite}
            setAdresseSite={setAdresseSite}
            pdlParticulier={pdlParticulier}
            setPdlParticulier={setPdlParticulier}
            fournisseurActuel={fournisseurActuel}
            setFournisseurActuel={setFournisseurActuel}
            fournisseurFutur={fournisseurFutur}
            setFournisseurFutur={setFournisseurFutur}
            offreParticulier={offreParticulier}
            setOffreParticulier={setOffreParticulier}
            postesAffichesParticulier={postesAffichesParticulier}
            handlePosteParticulierChange={handlePosteParticulierChange}
            abonnementParticulier={abonnementParticulier}
            setAbonnementParticulier={setAbonnementParticulier}
            totalConsoParticulierKWh={totalConsoParticulierKWh}
            totalParticulierActuel={totalParticulierActuel}
            totalParticulierFutur={totalParticulierFutur}
            consoGazParticulier={consoGazParticulier}
            setConsoGazParticulier={setConsoGazParticulier}
            abonnementGazParticulier={abonnementGazParticulier}
            setAbonnementGazParticulier={setAbonnementGazParticulier}
            consoGazParticulierKWh={consoGazParticulierKWh}
            totalGazParticulierActuel={totalGazParticulierActuel}
            totalGazParticulierFutur={totalGazParticulierFutur}
            formatNumber={formatNumber}
            calculateBudget={calculateBudget}
          />
        )}

        {/* Informations Générales - Mode Professionnel uniquement */}
        {clientType === 'pro' && (
          <InformationsGenerales
            energyType={energyType}
            clientName={clientName}
            setClientName={setClientName}
            commercialName={commercialName}
            setCommercialName={setCommercialName}
            puissanceSouscriteInput={puissanceSouscriteInput}
            setPuissanceSouscriteInput={setPuissanceSouscriteInput}
            isPuissanceValide={isPuissanceValide}
            puissanceAtteinteInput={puissanceAtteinteInput}
            setPuissanceAtteinteInput={setPuissanceAtteinteInput}
            segmentElec={segmentElec}
            setSegmentElec={setSegmentElec}
            optionTarifaire={optionTarifaire}
            setOptionTarifaire={setOptionTarifaire}
            nafCode={nafCode}
            setNafCode={setNafCode}
            segmentGaz={segmentGaz}
            setSegmentGaz={setSegmentGaz}
            isNafIndustrielGaz={isNafIndustrielGaz}
            capaciteJournaliereT4={capaciteJournaliereT4}
            setCapaciteJournaliereT4={setCapaciteJournaliereT4}
            niveauCapaciteT4={niveauCapaciteT4}
            setNiveauCapaciteT4={setNiveauCapaciteT4}
          />
        )}

        {/* SECTION PARTICULIER (info + conso) déplacée vers <ParticulierSection /> au-dessus */}

        {/* Consommation par Poste - Électricité (Mode Pro) */}
        {energyType === 'electricity' && clientType === 'pro' && (
        <section className="card" data-testid="conso-section">
          <h2 className="section-title">
            Consommation par Poste <span className="annual-badge">Budget Annuel</span>
          </h2>
          <div className="table-container">
            <table className="data-table" data-testid="conso-table">
              <thead>
                <tr>
                  <th>Poste</th>
                  <th>Conso (MWh)</th>
                  <th className="th-with-toggle">
                    <div className="th-toggle-container">
                      <button 
                        className={`toggle-btn ${typeBudgetConso === 'actuel' ? 'active' : ''}`}
                        onClick={() => setTypeBudgetConso('actuel')}
                      >
                        Prix Actuel
                      </button>
                      <button 
                        className={`toggle-btn ${typeBudgetConso === 'courtage' ? 'active' : ''}`}
                        onClick={() => setTypeBudgetConso('courtage')}
                      >
                        Prix Courtage
                      </button>
                    </div>
                    <span className="th-label">(€/MWh)</span>
                  </th>
                  <th>{typeBudgetConso === 'actuel' ? 'Budget Actuel (€)' : 'Budget Courtage (€)'}</th>
                  <th>Prix Futur (€/MWh)</th>
                  <th>Budget Futur (€)</th>
                </tr>
              </thead>
              <tbody>
                {postes.map((poste) => (
                  <tr key={poste.id}>
                    <td className="poste-label">{poste.label}</td>
                    <td>
                      <input
                        type="number"
                        value={poste.conso}
                        onChange={(e) => updatePoste(poste.id, 'conso', e.target.value)}
                        placeholder="0"
                        step="0.01"
                        data-testid={`conso-${poste.id.toLowerCase()}-input`}
                      />
                    </td>
                    <td>
                      {typeBudgetConso === "actuel" ? (
                        <input
                          type="number"
                          value={poste.prixActuel}
                          onChange={(e) => updatePoste(poste.id, 'prixActuel', e.target.value)}
                          placeholder="0"
                          step="any"
                          data-testid={`prix-actuel-${poste.id.toLowerCase()}-input`}
                        />
                      ) : (
                        <input
                          type="number"
                          value={poste.prixCourtage}
                          onChange={(e) => updatePoste(poste.id, 'prixCourtage', e.target.value)}
                          placeholder="0"
                          step="any"
                          className="input-courtage"
                          data-testid={`prix-courtage-${poste.id.toLowerCase()}-input`}
                        />
                      )}
                    </td>
                    <td className="calculated">{formatNumber(calculateBudget(poste.conso, getPrixActuelEffectif(poste)))}</td>
                    <td>
                      <input
                        type="number"
                        value={poste.prixFutur}
                        onChange={(e) => updatePoste(poste.id, 'prixFutur', e.target.value)}
                        placeholder="0"
                        step="any"
                        data-testid={`prix-futur-${poste.id.toLowerCase()}-input`}
                      />
                    </td>
                    <td className="calculated">{formatNumber(calculateBudget(poste.conso, poste.prixFutur))}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td>Total Consommation</td>
                  <td className="total-value">{formatNumber(totalConsoMWh)} MWh</td>
                  <td className="total-value avg-price">{formatPrice(prixMoyenActuel)} €/MWh</td>
                  <td className="total-value">{formatNumber(totalConsoActuel)}</td>
                  <td className="total-value avg-price">{formatPrice(prixMoyenFutur)} €/MWh</td>
                  <td className="total-value">{formatNumber(totalConsoFutur)}</td>
                </tr>
                {/* Ligne Optimisation Energy Manager */}
                <tr className={`energy-manager-row ${showEnergyManager ? 'active' : ''}`}>
                  <td colSpan="3">
                    <label className="energy-manager-label">
                      <input
                        type="checkbox"
                        checked={showEnergyManager}
                        onChange={(e) => setShowEnergyManager(e.target.checked)}
                        data-testid="energy-manager-checkbox"
                      />
                      <span className="energy-manager-text">
                        Optimisation de consommation (Energy Manager) - 5%
                      </span>
                    </label>
                  </td>
                  <td className="calculated">-</td>
                  <td className="calculated">-5%</td>
                  <td className={`calculated energy-manager-value ${showEnergyManager ? 'active' : ''}`}>
                    {showEnergyManager ? `-${formatNumber(gainEnergyManager)}` : '-'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Alerte audit énergétique obligatoire */}
          {totalConsoMWh >= 2750 && (
            <div className="audit-alert" data-testid="audit-alert">
              <div className="audit-alert-icon">
                <AlertTriangle size={24} />
              </div>
              <div className="audit-alert-content">
                <h4>Audit énergétique obligatoire</h4>
                <p>
                  Votre consommation annuelle de <strong>{formatNumber(totalConsoMWh)} MWh</strong> dépasse le seuil de 2 750 MWh.
                  <br />
                  Un <strong>audit énergétique est obligatoire</strong> pour votre entreprise.
                </p>
                <p className="audit-alert-warning">
                  En cas de non-réalisation de cet audit, une <strong>amende forfaitaire de 75 000 €</strong> s'applique.
                </p>
              </div>
            </div>
          )}
        </section>
        )}

        {/* Consommation Gaz - Un seul poste (Mode Pro) */}
        {energyType === 'gas' && clientType === 'pro' && (
        <section className="card" data-testid="conso-gaz-section">
          <h2 className="section-title">
            <Flame size={20} className="section-icon" style={{ color: '#F97316' }} />
            Part Consommation Gaz <span className="annual-badge">Budget Annuel</span>
          </h2>
          <div className="table-container">
            <table className="data-table" data-testid="conso-gaz-table">
              <thead>
                <tr>
                  <th>Poste</th>
                  <th>Conso (MWh)</th>
                  <th>Prix Actuel (€/MWh)</th>
                  <th>Budget Actuel (€)</th>
                  <th>Prix Futur (€/MWh)</th>
                  <th>Budget Futur (€)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="poste-label">Part Consommation</td>
                  <td>
                    <input
                      type="number"
                      value={consoGaz.conso}
                      onChange={(e) => setConsoGaz({ ...consoGaz, conso: e.target.value })}
                      placeholder="0"
                      step="0.01"
                      data-testid="conso-gaz-input"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={consoGaz.prixActuel}
                      onChange={(e) => setConsoGaz({ ...consoGaz, prixActuel: e.target.value })}
                      placeholder="0"
                      step="any"
                      data-testid="prix-actuel-gaz-input"
                    />
                  </td>
                  <td className="calculated">{formatNumber(budgetConsoGazActuel)}</td>
                  <td>
                    <input
                      type="number"
                      value={consoGaz.prixFutur}
                      onChange={(e) => setConsoGaz({ ...consoGaz, prixFutur: e.target.value })}
                      placeholder="0"
                      step="any"
                      data-testid="prix-futur-gaz-input"
                    />
                  </td>
                  <td className="calculated">{formatNumber(budgetConsoGazFutur)}</td>
                </tr>
                <tr className="total-row">
                  <td>Total Consommation</td>
                  <td className="total-value">{formatNumber(consoGazMWh)} MWh</td>
                  <td className="total-value avg-price">{formatPrice(prixMoyenGazActuel)} €/MWh</td>
                  <td className="total-value">{formatNumber(budgetConsoGazActuel)}</td>
                  <td className="total-value avg-price">{formatPrice(prixMoyenGazFutur)} €/MWh</td>
                  <td className="total-value">{formatNumber(budgetConsoGazFutur)}</td>
                </tr>
                {/* Ligne Optimisation Energy Manager */}
                <tr className={`energy-manager-row ${showEnergyManager ? 'active' : ''}`}>
                  <td colSpan="3">
                    <label className="energy-manager-label">
                      <input
                        type="checkbox"
                        checked={showEnergyManager}
                        onChange={(e) => setShowEnergyManager(e.target.checked)}
                        data-testid="energy-manager-checkbox-gaz"
                      />
                      <span className="energy-manager-text">
                        Optimisation de consommation (Energy Manager) - 5%
                      </span>
                    </label>
                  </td>
                  <td className="calculated">-</td>
                  <td className="calculated">-5%</td>
                  <td className={`calculated energy-manager-value ${showEnergyManager ? 'active' : ''}`}>
                    {showEnergyManager ? `-${formatNumber(gainEnergyManager)}` : '-'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Alerte audit énergétique pour Gaz */}
          {consoGazMWh >= 2750 && (
            <div className="audit-alert" data-testid="audit-alert-gaz">
              <div className="audit-alert-icon">
                <AlertTriangle size={24} />
              </div>
              <div className="audit-alert-content">
                <h4>Audit énergétique obligatoire</h4>
                <p>
                  Votre consommation annuelle de gaz de <strong>{formatNumber(consoGazMWh)} MWh</strong> dépasse le seuil de 2 750 MWh.
                </p>
              </div>
            </div>
          )}
        </section>
        )}

        {/* Consommation Particulier déplacée vers <ParticulierSection /> en haut */}

        {/* Abonnement & Prestations (Mode Pro uniquement) */}
        {clientType === 'pro' && (
        <section className="card" data-testid="abo-section">
          <h2 className="section-title">
            Abonnement & Prestations <span className="annual-badge">Budget Annuel</span>
          </h2>
          <div className="table-container">
            <table className="data-table" data-testid="abo-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Quantité</th>
                  <th className="th-with-toggle">
                    <div className="th-toggle-container">
                      <button 
                        className={`toggle-btn ${typeBudgetAboPrest === 'actuel' ? 'active' : ''}`}
                        onClick={() => setTypeBudgetAboPrest('actuel')}
                      >
                        Prix Actuel
                      </button>
                      <button 
                        className={`toggle-btn ${typeBudgetAboPrest === 'courtage' ? 'active' : ''}`}
                        onClick={() => setTypeBudgetAboPrest('courtage')}
                      >
                        Prix Courtage
                      </button>
                    </div>
                    <span className="th-label">(€/an)</span>
                  </th>
                  <th>{typeBudgetAboPrest === 'actuel' ? 'Budget Actuel (€/an)' : 'Budget Courtage (€/an)'}</th>
                  <th>Prix Futur (€/an)</th>
                  <th>Budget Futur (€/an)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="poste-label">Abonnement</td>
                  <td>
                    <input
                      type="number"
                      value={currentAbonnement.quantite}
                      onChange={(e) => setCurrentAbonnement({ ...currentAbonnement, quantite: parseInt(e.target.value) || 1 })}
                      min="1"
                      data-testid="abo-quantite-input"
                    />
                  </td>
                  <td>
                    {typeBudgetAboPrest === "actuel" ? (
                      <input
                        type="number"
                        value={currentAbonnement.prixActuel}
                        onChange={(e) => setCurrentAbonnement({ ...currentAbonnement, prixActuel: e.target.value })}
                        placeholder="0"
                        step="0.01"
                        data-testid="abo-prix-actuel-input"
                      />
                    ) : (
                      <input
                        type="number"
                        value={currentAbonnement.prixCourtage}
                        onChange={(e) => setCurrentAbonnement({ ...currentAbonnement, prixCourtage: e.target.value })}
                        placeholder="0"
                        step="0.01"
                        className="input-courtage"
                        data-testid="abo-prix-courtage-input"
                      />
                    )}
                  </td>
                  <td className="calculated">{formatNumber(getPrixAboPrestActuel(currentAbonnement) * currentAbonnement.quantite)}</td>
                  <td>
                    <input
                      type="number"
                      value={currentAbonnement.prixFutur}
                      onChange={(e) => setCurrentAbonnement({ ...currentAbonnement, prixFutur: e.target.value })}
                      placeholder="0"
                      step="0.01"
                      data-testid="abo-prix-futur-input"
                    />
                  </td>
                  <td className="calculated">{formatNumber((parseFloat(currentAbonnement.prixFutur) || 0) * currentAbonnement.quantite)}</td>
                </tr>
                <tr>
                  <td className="poste-label">Prestation</td>
                  <td>
                    <input
                      type="number"
                      value={currentPrestation.quantite}
                      onChange={(e) => setCurrentPrestation({ ...currentPrestation, quantite: parseInt(e.target.value) || 1 })}
                      min="1"
                      data-testid="prest-quantite-input"
                    />
                  </td>
                  <td>
                    {typeBudgetAboPrest === "actuel" ? (
                      <input
                        type="number"
                        value={currentPrestation.prixActuel}
                        onChange={(e) => setCurrentPrestation({ ...currentPrestation, prixActuel: e.target.value })}
                        placeholder="0"
                        step="any"
                        data-testid="prest-prix-actuel-input"
                      />
                    ) : (
                      <input
                        type="number"
                        value={currentPrestation.prixCourtage}
                        onChange={(e) => setCurrentPrestation({ ...currentPrestation, prixCourtage: e.target.value })}
                        placeholder="0"
                        step="any"
                        className="input-courtage"
                        data-testid="prest-prix-courtage-input"
                      />
                    )}
                  </td>
                  <td className="calculated">{formatNumber(getPrixAboPrestActuel(currentPrestation) * currentPrestation.quantite)}</td>
                  <td>
                    <input
                      type="number"
                      value={currentPrestation.prixFutur}
                      onChange={(e) => setCurrentPrestation({ ...currentPrestation, prixFutur: e.target.value })}
                      placeholder="0"
                      step="any"
                      data-testid="prest-prix-futur-input"
                    />
                  </td>
                  <td className="calculated">{formatNumber((parseFloat(currentPrestation.prixFutur) || 0) * currentPrestation.quantite)}</td>
                </tr>
                <tr className="total-row">
                  <td colSpan="3">Total Abonnement & Prestations</td>
                  <td className="total-value">{formatNumber(totalAboPrestActuel)}</td>
                  <td></td>
                  <td className="total-value">{formatNumber(totalAboPrestFutur)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
        )}

        {/* Acheminement (TURPE Élec + CTA / ATRD Gaz) - Mode Pro */}
        {clientType === 'pro' && (
          <AcheminementSection
            energyType={energyType}
            showAcheminement={showAcheminement}
            setShowAcheminement={setShowAcheminement}
            formatNumber={formatNumber}
            optionTarifaire={optionTarifaire}
            puissanceSouscrite={puissanceSouscrite}
            segmentElec={segmentElec}
            totalConsoMWh={totalConsoMWh}
            acheminementActuelCalc={acheminementActuelCalc}
            acheminementFuturCalc={acheminementFuturCalc}
            segmentGaz={segmentGaz}
            segmentGazFutur={segmentGazFutur}
            setSegmentGazFutur={setSegmentGazFutur}
            capaciteJournaliereT4Futur={capaciteJournaliereT4Futur}
            setCapaciteJournaliereT4Futur={setCapaciteJournaliereT4Futur}
            niveauCapaciteT4Futur={niveauCapaciteT4Futur}
            setNiveauCapaciteT4Futur={setNiveauCapaciteT4Futur}
            consoGazMWh={consoGazMWh}
            ATRD={ATRD}
            capaciteT4={capaciteT4}
            capaciteT4Fut={capaciteT4Fut}
            atrdActuel={atrdActuel}
            atrdFutur={atrdFutur}
          />
        )}


        {/* Accise (Élec ou TICGN Gaz) - Mode Pro */}
        {clientType === 'pro' && (
          <AcciseSection
            energyType={energyType}
            showAccise={showAccise}
            setShowAccise={setShowAccise}
            formatNumber={formatNumber}
            totalConsoMWh={totalConsoMWh}
            puissanceSouscrite={puissanceSouscrite}
            nafCode={nafCode}
            isNafEntre4et39={isNafEntre4et39}
            tauxAccise={tauxAccise}
            tauxAcciseFutur={tauxAcciseFutur}
            forceTauxPlein={forceTauxPlein}
            setForceTauxPlein={setForceTauxPlein}
            acciseAutoActuel={acciseAutoActuel}
            acciseAutoFutur={acciseAutoFutur}
            consoGazMWh={consoGazMWh}
            isNafIndustrielGaz={isNafIndustrielGaz}
            tauxAcciseGazActuel={tauxAcciseGazActuel}
            tauxAcciseGazFutur={tauxAcciseGazFutur}
            forceAcciseGazTauxPlein={forceAcciseGazTauxPlein}
            setForceAcciseGazTauxPlein={setForceAcciseGazTauxPlein}
            ACCISE_GAZ_TAUX_STANDARD={ACCISE_GAZ_TAUX_STANDARD}
            ACCISE_GAZ_TAUX_REDUIT={ACCISE_GAZ_TAUX_REDUIT}
            acciseGazActuel={acciseGazActuel}
            acciseGazFutur={acciseGazFutur}
          />
        )}


        {/* Effacement Électrique - Mode Pro Élec uniquement */}
        {energyType === 'electricity' && clientType === 'pro' && (
          <EffacementSection
            showEffacement={showEffacement}
            setShowEffacement={setShowEffacement}
            isNafEntre4et39={isNafEntre4et39}
            isPuissanceValide={isPuissanceValide}
            puissanceSouscrite={puissanceSouscrite}
            effacement={effacement}
            setEffacement={setEffacement}
            puissanceEffacableKW={puissanceEffacableKW}
            gainCapacite={gainCapacite}
            gainNEBEF={gainNEBEF}
            gainTotalEffacement={gainTotalEffacement}
            gainMin={gainMin}
            gainMax={gainMax}
            formatNumber={formatNumber}
          />
        )}


        {/* Actions */}
        <div className="actions-row">
          {/* Bouton Export PDF - différent selon Pro ou Particulier */}
          {clientType === 'pro' ? (
            <button onClick={handleExportPDF} className="btn btn-primary btn-large" data-testid="export-pdf-btn">
              <FileDown size={20} />
              Exporter PDF Pro
            </button>
          ) : (
            <button onClick={handleExportPDFParticulier} className="btn btn-primary btn-large" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)' }} data-testid="export-pdf-particulier-btn">
              <FileDown size={20} />
              Exporter PDF Particulier
            </button>
          )}
          <button onClick={handleReset} className="btn btn-secondary btn-large" data-testid="reset-btn">
            <RotateCcw size={20} />
            Réinitialiser
          </button>
        </div>

        {/* Comparatif Budget - Mode Pro uniquement */}
        {clientType === 'pro' && (
          <ComparatifSection
            budgetActuel={budgetActuel}
            budgetFutur={budgetFutur}
            ecart={ecart}
            ecartPourcent={ecartPourcent}
            gainEffacementApplique={gainEffacementApplique}
            gainEnergyManager={gainEnergyManager}
            formatNumber={formatNumber}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <a href="https://placedesenergies.com" target="_blank" rel="noopener noreferrer">
          Place des Énergies - www.placedesenergies.com
        </a>
      </footer>
    </div>
  );
}

export default AppWrapper;
